import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { RouterModule } from '@angular/router';
import { FullMaterialModule } from 'src/app/material/material-module';

interface StatItem {
  value: string;
  label: string;
  icon?: string;
}

interface BulkOption {
  title: string;
  description: string;
  detailedDescription: string;
  icon: string;
  stats: StatItem[];
  documentationLink: string | undefined;
  getStartedLink?: string;
  intercomMessage?: string;
}

@Component({
  selector: 'app-bulk-content',
  templateUrl: './bulk-content.component.html',
  styleUrls: ['./bulk-content.component.scss'],
  imports: [FullMaterialModule, CommonModule, RouterModule],
})
export class BulkContentComponent {
  bulkOptions: BulkOption[] = [
    {
      title: 'Job Change Firehose',
      description:
        'A daily or weekly delivery of all job changes detected by our system, ready for processing by your own systems.',
      detailedDescription:
        'The Job Change Firehose provides enterprise-grade streaming access to professional job changes as they happen. Built for high-volume integrations, this service delivers comprehensive updates including company changes, title modifications, and career transitions across our entire database. The firehose can be filtered to include only the job changes you are interested in.',
      icon: 'trending_up',
      stats: [
        { value: '~1M', label: 'Job Changes/mo', icon: 'swap_horiz' },
        { value: '~500K', label: 'Title Changes/mo', icon: 'badge' },
        { value: '$10K', label: 'per month', icon: 'payments' },
      ],
      documentationLink: 'https://docs.gotlivedata.com/docs/job-change-firehose#/',
      intercomMessage:
        "Hi! I'm interested in learning more about the Job Change Firehose. Can you help me get started?",
    },
    {
      title: 'People API',
      description:
        'On-demand API access to all People data, including endpoints for enrichment, validation, and discovery.',
      detailedDescription:
        'Our People API provides RESTful access to the most up-to-date professional database available. With flexible querying capabilities, webhook support, and bulk export options, integrate people data directly into your applications and workflows to ensure data accuracy before processing.',
      icon: 'api',
      stats: [
        { value: '~100M', label: 'Professional Profiles', icon: 'groups' },
        { value: '10 days', label: 'Average Refresh Rate', icon: 'update' },
        { value: 'Tiered', label: 'Flexible Pricing Plans', icon: 'payments' },
      ],
      documentationLink: 'https://docs.gotlivedata.com/docs/people-api#/',
      getStartedLink: '/people/search',
    },
    {
      title: 'Contact Job Change Tracking',
      description:
        "Get notified when contacts of your choosing change jobs, helping you keep your and your clients' data up to date.",
      detailedDescription:
        'Contact Job Change Tracking allows you to upload your existing contact databases and continuously monitor them for career changes. We match, enhance, and track your contacts, providing alerts and reports when changes occur. Perfect for sales teams, recruiters, and relationship managers.',
      icon: 'account_circle',
      stats: [
        { value: '~1%', label: 'Job Changes per Month', icon: 'swap_horiz' },
        { value: '~16%', label: 'Initial Correction Rate', icon: 'fact_check' },
        { value: 'Realtime', label: 'Change Notifications', icon: 'notifications' },
      ],
      documentationLink: 'https://docs.gotlivedata.com/docs/ledgers#/',
      intercomMessage:
        "Hi! I'm interested in learning more about tracking job changes for specific contacts. Can you help me get started?",
    },
    {
      title: 'Company Workforce Tracking',
      description:
        'Roll-up workforce tracking for companies of your choosing, providing arrival and departure counts broken into granular categories.',
      detailedDescription:
        'Company Workforce Tracking provides comprehensive monitoring of target companies and their personnel with no PII. Track employee movements in and out of organizations, monitor department-level changes, and receive insights about organizational growth or restructuring. Essential for account-based marketing and sales strategies.',
      icon: 'business',
      stats: [
        { value: 'Custom', label: 'Account Lists', icon: 'list_alt' },
        { value: 'Daily', label: 'Update Frequency', icon: 'schedule' },
        { value: 'Enterprise', label: 'Pricing Available', icon: 'corporate_fare' },
      ],
      documentationLink: undefined,
      intercomMessage:
        "Hi! I'm interested in learning more about Company Workforce Tracking. Can you help me get started?",
    },
  ];

  handleGetStarted(option: BulkOption): void {
    if (option.intercomMessage && (window as any).Intercom) {
      (window as any).Intercom('update', { skip_fin: true });
      (window as any).Intercom('showNewMessage', option.intercomMessage);
    }
  }

  scheduleConsultation(): void {
    if ((window as any).Intercom) {
      (window as any).Intercom('update', { skip_fin: true });
      (window as any).Intercom(
        'showNewMessage',
        'Hi! I would like to schedule a consultation to discuss bulk data access options and determine the best solution for my use case.'
      );
    }
  }
}
